import { useEffect, useMemo, useState } from 'react';
import { Edit, Search } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageContainer } from '../../../shared/components/PageContainer';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { useEstados, useMunicipios } from '../../geo/hooks';
import { mensagemDeErro, statusDoErro } from '../api';
import { consultarCep } from '../api/cadastro';
import { LocationItem } from '../api/ferias';
import { formatCep } from '../components/format';
import { PessoasModal } from '../components/PessoasModal';
import { useDefinirEnderecoLocalidade, useLocalidades } from '../hooks/ferias';

/**
 * Endereço das localidades de trabalho (change `pessoas-ferias`): o
 * município do endereço define os feriados de quem está lotado ali.
 */
export default function EnderecosLocalidadesPage() {
  const localidades = useLocalidades();
  const [editing, setEditing] = useState<LocationItem | null>(null);
  const lista = localidades.data ?? [];

  return (
    <PageContainer
      title="Endereços das localidades"
      subtitle="Sem endereço na localidade, as férias usam o endereço residencial do colaborador para os feriados."
      breadcrumbs={[{ label: 'Pessoas' }, { label: 'Endereços das localidades' }]}
    >
      {lista.length === 0 ? (
        <EmptyState title="Nenhuma localidade ativa" />
      ) : (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
          {lista.map((l) => (
            <li key={l.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
              <span>
                <span className="font-medium text-gray-900">{l.name}</span> <span className="font-mono text-xs text-gray-500">{l.code}</span>
                <span className="block text-gray-600">
                  {l.address
                    ? `${l.address.street}, ${l.address.number}${l.address.complement ? ` — ${l.address.complement}` : ''} · ${l.address.district} · ${l.address.cityName ?? ''} · CEP ${formatCep(l.address.zipCode)}`
                    : <span className="text-amber-700">Sem endereço</span>}
                </span>
              </span>
              <button type="button" className="rounded p-1.5 text-gray-500 hover:bg-gray-100" onClick={() => setEditing(l)} aria-label={`Editar endereço de ${l.name}`}>
                <Edit className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <EnderecoModal location={editing} onClose={() => setEditing(null)} />
    </PageContainer>
  );
}

interface Form { zipCode: string; street: string; number: string; complement: string; district: string; stateUf: string; cityId: number }
const VAZIO: Form = { zipCode: '', street: '', number: '', complement: '', district: '', stateUf: '', cityId: 0 };

function EnderecoModal({ location, onClose }: { location: LocationItem | null; onClose: () => void }) {
  const toast = useToast();
  const salvar = useDefinirEnderecoLocalidade();
  const [value, setValue] = useState<Form>(VAZIO);
  const [error, setError] = useState<string | null>(null);
  const [cepInfo, setCepInfo] = useState<string | null>(null);
  const estados = useEstados();
  const municipios = useMunicipios(value.stateUf || null);
  const cidades = useMemo(() => (municipios.data ?? []).slice().sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')), [municipios.data]);

  useEffect(() => {
    if (!location) return;
    const a = location.address;
    const uf = a?.cityName?.split('/')[1] ?? '';
    setValue(a ? { zipCode: a.zipCode, street: a.street, number: a.number, complement: a.complement ?? '', district: a.district, stateUf: uf, cityId: a.cityId } : VAZIO);
    setError(null);
    setCepInfo(null);
  }, [location]);

  async function buscarCep() {
    setCepInfo(null);
    try {
      const r = await consultarCep(value.zipCode);
      setValue((v) => ({
        ...v, street: r.street || v.street, district: r.district || v.district, stateUf: r.stateUf || v.stateUf,
        cityId: r.cityId ?? v.cityId, complement: v.complement || r.complement || '',
      }));
    } catch (err) {
      setCepInfo(statusDoErro(err) === 404 ? 'CEP não encontrado.' : 'Consulta de CEP indisponível. Preencha manualmente.');
    }
  }

  async function submit() {
    if (!location) return;
    if (value.zipCode.replace(/\D/g, '').length !== 8) return setError('Informe o CEP com 8 dígitos.');
    if (!value.street.trim() || !value.number.trim() || !value.district.trim() || !value.cityId) {
      return setError('Preencha logradouro, número, bairro, UF e município.');
    }
    try {
      await salvar.mutateAsync({
        id: location.id,
        address: {
          zipCode: value.zipCode.replace(/\D/g, ''), street: value.street.trim(), number: value.number.trim(),
          complement: value.complement.trim() || undefined, district: value.district.trim(), cityId: value.cityId,
        },
      });
      toast.success('Endereço da localidade salvo.');
      onClose();
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível salvar o endereço.'));
    }
  }

  return (
    <PessoasModal open={location !== null} size="lg" title={`Endereço — ${location?.name ?? ''}`} submitLabel="Salvar"
      isSubmitting={salvar.isPending} submitError={error} onClose={onClose} onSubmit={submit}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextInput
          label="CEP"
          value={formatCep(value.zipCode)}
          onChange={(e) => setValue((x) => ({ ...x, zipCode: e.target.value }))}
          onBlur={() => value.zipCode.replace(/\D/g, '').length === 8 && buscarCep()}
          rightElement={<button type="button" onClick={buscarCep} className="text-gray-500" aria-label="Buscar CEP"><Search className="h-4 w-4" /></button>}
        />
        <TextInput label="Logradouro" value={value.street} onChange={(e) => setValue((x) => ({ ...x, street: e.target.value }))} />
        <TextInput label="Número" value={value.number} onChange={(e) => setValue((x) => ({ ...x, number: e.target.value }))} />
        <TextInput label="Complemento" value={value.complement} onChange={(e) => setValue((x) => ({ ...x, complement: e.target.value }))} />
        <TextInput label="Bairro" value={value.district} onChange={(e) => setValue((x) => ({ ...x, district: e.target.value }))} />
        <AutocompleteSelect
          label="UF"
          options={(estados.data ?? []).map((e) => ({ value: e.uf, label: `${e.uf} — ${e.nome}` }))}
          value={value.stateUf}
          onChange={(v) => setValue((x) => ({ ...x, stateUf: v, cityId: 0 }))}
        />
        <AutocompleteSelect
          label="Município"
          disabled={!value.stateUf}
          options={cidades.map((m) => ({ value: String(m.idIbge), label: m.nome }))}
          value={value.cityId ? String(value.cityId) : ''}
          onChange={(v) => setValue((x) => ({ ...x, cityId: Number(v) }))}
        />
      </div>
      {cepInfo && <p className="text-xs text-amber-700">{cepInfo}</p>}
    </PessoasModal>
  );
}
